import { useCallback, useEffect, useState } from 'react';
import { Spinner } from '../Spinner';
import { Button } from '../ui/Button';
import { COLORS } from '../../constants/theme';
import { useVisibilityAwarePolling } from '../../hooks/useVisibilityAwarePolling';
import { fetchAmbushableAttacks } from '../../api/attack';
import { formatNumber } from '../../utils/format';
import type { AmbushableAttack } from '../../types/attack';

interface AmbushSourceSelectorProps {
  worldId: string;
  provinceId: string;
  currentTick: number;
  selectedSourceEventSeq: number | null;
  onSelect: (attack: AmbushableAttack) => void;
  /** Bump this to force an immediate refetch (e.g. after an AMBUSH_NOT_ELIGIBLE submit — the list went stale). */
  refreshTrigger?: number;
}

const POLL_INTERVAL_MS = 30_000;
const POLL_JITTER_MS = 5_000;

/**
 * SF-96 Component 9: replaces {@link TargetSelector} inline (Attack.tsx) when the player selects
 * Ambush — there is no free-target-choice mode for Ambush; the target is derived from the
 * chosen source row. Fetches the PG-backed `/ambushable-attacks` display endpoint.
 */
export function AmbushSourceSelector({
  worldId,
  provinceId,
  currentTick,
  selectedSourceEventSeq,
  onSelect,
  refreshTrigger,
}: AmbushSourceSelectorProps) {
  const [attacks, setAttacks] = useState<AmbushableAttack[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(() => {
    fetchAmbushableAttacks(worldId, provinceId)
      .then((result) => {
        setAttacks(result);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load ambushable attacks');
      })
      .finally(() => setIsLoading(false));
  }, [worldId, provinceId]);

  useEffect(() => {
    setIsLoading(true);
    load();
  }, [load, refreshTrigger]);

  useVisibilityAwarePolling(load, POLL_INTERVAL_MS, POLL_JITTER_MS);

  const handleKeyDown = (e: React.KeyboardEvent, list: AmbushableAttack[], index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(list[index]);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      const next = list[(index + 1) % list.length];
      document.getElementById(`ambush-source-${next.sourceEventSeq}`)?.focus();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = list[(index - 1 + list.length) % list.length];
      document.getElementById(`ambush-source-${prev.sourceEventSeq}`)?.focus();
    }
  };

  return (
    <div className="rounded" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, padding: '1rem' }}>
      <h2 className="text-lg font-semibold mb-3" style={{ color: COLORS.text.primary }}>Select Attack to Ambush</h2>

      {isLoading && (
        <div className="flex items-center gap-2 py-4">
          <Spinner size="sm" />
          <span className="text-sm" style={{ color: COLORS.text.tertiary }}>Loading ambushable attacks…</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="flex items-center gap-3 py-4">
          <span className="text-sm" style={{ color: COLORS.negative }}>{error}</span>
          <Button onClick={load} variant="secondary" className="text-xs py-1 px-2">Retry</Button>
        </div>
      )}

      {!isLoading && !error && attacks !== null && attacks.length === 0 && (
        <div className="text-sm py-2" style={{ color: COLORS.text.tertiary }}>
          No one has raided you recently — Ambush opportunities appear here after a Traditional March attack lands against you.
        </div>
      )}

      {!isLoading && !error && attacks !== null && attacks.length > 0 && (
        <div role="radiogroup" aria-label="Ambushable attacks" className="space-y-2">
          {attacks.map((attack, index) => {
            const isSelected = attack.sourceEventSeq === selectedSourceEventSeq;
            const daysRemaining = Math.max(0, attack.expiresAtTick - currentTick);
            return (
              <div
                key={attack.sourceEventSeq}
                id={`ambush-source-${attack.sourceEventSeq}`}
                role="radio"
                aria-checked={isSelected}
                tabIndex={index === 0 ? 0 : -1}
                onClick={() => onSelect(attack)}
                onKeyDown={(e) => handleKeyDown(e, attacks, index)}
                className="rounded px-3 py-2 cursor-pointer outline-none"
                style={{
                  border: `2px solid ${isSelected ? COLORS.link : COLORS.border}`,
                  backgroundColor: isSelected ? 'rgba(106, 158, 142, 0.12)' : COLORS.cardAlt,
                }}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium" style={{ color: COLORS.text.primary }}>
                    {attack.attackerProvinceName} ({attack.attackerLocation})
                  </span>
                  <span className="text-xs" style={{ color: COLORS.text.muted }}>
                    Expires in {formatNumber(daysRemaining)} days
                  </span>
                </div>
                <div className="text-xs mt-1" style={{ color: COLORS.text.secondary }}>
                  Raided {formatNumber(attack.landCaptured)} acres · Recapture ~{formatNumber(attack.recaptureAmount)} acres
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
